import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    IconButton,
    Menu,
    MenuItem,
    Chip,
    TextField,
    InputAdornment,
    CircularProgress,
    Stack,
    Tooltip,
    Snackbar,
    TablePagination,
    Alert,
    FormControl,
    InputLabel,
    Select,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    MoreVert as MoreVertIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Event as EventIcon,
    Visibility as VisibilityIcon,
    Edit as EditIcon,
    Block as BlockIcon,
    CheckCircle as CheckCircleIcon,
    Delete as DeleteIcon,
    Add as AddIcon
} from '@mui/icons-material';
import api from '../../../services/api';
import EventDetailsModal from './EventDetailsModal';
import EventEditModal from './EventEditModal';

// Event statuses we assume backend supports
// Adjust if API differs
const STATUS_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'UPCOMING', label: 'Upcoming' },
    { value: 'ONGOING', label: 'Ongoing' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'CANCELLED', label: 'Cancelled' },
    { value: 'DRAFT', label: 'Draft' }
];

const AdminEventManagement = () => {
    // Data state
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEvent, setSelectedEvent] = useState(null);

    // UI state
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchBy, setSearchBy] = useState('title'); // title | host | location | id
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearch, setAppliedSearch] = useState({ term: '', by: 'title' });
    const [anchorEl, setAnchorEl] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Modals
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null); // 'activate' | 'suspend' | 'delete'

    // Pagination & sorting
    const [pagination, setPagination] = useState({
        page: 0,
        size: 10,
        totalElements: 0,
        sortField: 'createdAt',
        sortDirection: 'DESC'
    });

    const fetchEvents = async () => {
        try {
            setLoading(true);
            const params = {
                page: pagination.page,
                size: pagination.size,
                sort: `${pagination.sortField},${pagination.sortDirection}`
            };

            if (statusFilter !== 'all') params.status = statusFilter;
            if (appliedSearch.term) {
                params.search = appliedSearch.term;
                params.searchField = appliedSearch.by;
            }

            // Placeholder endpoint; adjust to real one
            const response = await api.get('/v1/api/events', { params }).catch(() => ({ data: { content: [], totalElements: 0, totalPages: 0 } }));

            // Response shape assumption; adapt if backend differs
            const data = response.data || { content: [], totalElements: 0, totalPages: 0 };
            setEvents(data.content);
            setPagination(prev => ({
                ...prev,
                totalElements: data.totalElements ?? 0,
                totalPages: data.totalPages ?? 0
            }));
        } catch (err) {
            showSnackbar('Failed to load events', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEvents();
    }, [pagination.page, pagination.size, pagination.sortField, pagination.sortDirection, statusFilter, appliedSearch]);

    const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

    const handleStatusChange = (e) => {
        setStatusFilter(e.target.value);
        setPagination(p => ({ ...p, page: 0 }));
    };

    const handleSubmitSearch = () => {
        setAppliedSearch({ term: searchTerm.trim(), by: searchBy });
        setPagination(p => ({ ...p, page: 0 }));
    };

    const handleRefresh = () => {
        setSearchTerm('');
        setAppliedSearch({ term: '', by: 'title' });
        setSearchBy('title');
        setStatusFilter('all');
        setPagination(p => ({ ...p, page: 0, sortField: 'createdAt', sortDirection: 'DESC' }));
        fetchEvents();
    };

    const handleSort = (field) => {
        const isAsc = pagination.sortField === field && pagination.sortDirection === 'ASC';
        setPagination(p => ({ ...p, sortField: field, sortDirection: isAsc ? 'DESC' : 'ASC', page: 0 }));
    };

    const getSortIcon = (field) => {
        if (pagination.sortField !== field) return null;
        return pagination.sortDirection === 'ASC' ? '▲' : '▼';
    };

    const handlePageChange = (e, newPage) => setPagination(p => ({ ...p, page: newPage }));
    const handleRowsChange = (e) => setPagination(p => ({ ...p, size: parseInt(e.target.value, 10), page: 0 }));

    // Menu & actions
    const handleMenuOpen = (e, eventItem) => {
        setAnchorEl(e.currentTarget);
        setSelectedEvent(eventItem);
    };
    const handleMenuClose = () => setAnchorEl(null);

    const openDetails = () => {
        setDetailsOpen(true);
        handleMenuClose();
    };
    const openEdit = () => {
        setEditOpen(true);
        handleMenuClose();
    };

    const requestAction = (action) => {
        setConfirmAction(action);
        setConfirmOpen(true);
        handleMenuClose();
    };
    const closeConfirm = () => {
        setConfirmOpen(false);
        setConfirmAction(null);
    };

    const performAction = async () => {
        if (!selectedEvent || !confirmAction) return;
        try {
            // Placeholder endpoints; adjust accordingly
            if (confirmAction === 'delete') {
                await api.delete(`/v1/api/events/${selectedEvent.id}`);
                showSnackbar('Event deleted', 'success');
            } else if (confirmAction === 'suspend') {
                await api.put(`/v1/api/events/${selectedEvent.id}/suspend`);
                showSnackbar('Event suspended', 'success');
            } else if (confirmAction === 'activate') {
                await api.put(`/v1/api/events/${selectedEvent.id}/activate`);
                showSnackbar('Event activated', 'success');
            }
            fetchEvents();
        } catch (err) {
            showSnackbar('Action failed', 'error');
        } finally {
            closeConfirm();
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'UPCOMING': return 'primary';
            case 'ONGOING': return 'success';
            case 'COMPLETED': return 'info';
            case 'CANCELLED': return 'error';
            case 'DRAFT': return 'warning';
            default: return 'default';
        }
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1500, mx: 'auto' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Event Management</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => showSnackbar('Add event flow TBD', 'info')}>
                    Create Event
                </Button>
            </Stack>

            {/* Filters */}
            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
                    {/* Unified search control */}
                    <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                        <FormControl size="small" sx={{ minWidth: 160, mr: 0, '& .MuiOutlinedInput-root': { borderTopRightRadius: 0, borderBottomRightRadius: 0 } }}>
                            <InputLabel id="search-by-label">Search by</InputLabel>
                            <Select labelId="search-by-label" value={searchBy} label="Search by" onChange={(e) => setSearchBy(e.target.value)}>
                                <MenuItem value="title">Title</MenuItem>
                                <MenuItem value="host">Host</MenuItem>
                                <MenuItem value="location">Location</MenuItem>
                                <MenuItem value="id">Event ID</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            placeholder={`Search by ${searchBy === 'id' ? 'Event ID' : searchBy.charAt(0).toUpperCase() + searchBy.slice(1)}...`}
                            variant="outlined"
                            size="small"
                            sx={{ ml: '-1px', mr: 0, maxWidth: 420, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSubmitSearch(); }}
                            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
                        />
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleSubmitSearch}
                            disabled={!searchTerm.trim()}
                            sx={{ height: '40px', ml: '-1px', borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                        >
                            Search
                        </Button>
                    </Box>

                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id="status-filter-label">Status</InputLabel>
                        <Select labelId="status-filter-label" value={statusFilter} label="Status" onChange={handleStatusChange}>
                            {STATUS_OPTIONS.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                        </Select>
                    </FormControl>

                    <Tooltip title="Refresh">
                        <IconButton onClick={handleRefresh} disabled={loading}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Paper>

            {/* Table */}
            <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
                ) : (
                    <>
                        <TableContainer sx={{ maxHeight: '65vh' }}>
                            <Table size="small" stickyHeader sx={{
                                '& .MuiTableCell-root': { py: 0.5, fontSize: '0.82rem' },
                                '& .MuiTableCell-head': { py: 0.5, fontSize: '0.8rem', fontWeight: 600 },
                                '& tbody tr:hover': { bgcolor: 'action.hover' },
                                '& tbody tr:nth-of-type(odd)': { bgcolor: 'background.default' }
                            }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell >ID </TableCell>
                                        <TableCell >Host </TableCell>
                                        <TableCell sx={{ cursor: 'pointer' }} onClick={() => handleSort('title')}>Title {getSortIcon('title')}</TableCell>
                                        <TableCell sx={{ cursor: 'pointer' }} onClick={() => handleSort('host')}>Category {getSortIcon('host')}</TableCell>
                                        <TableCell sx={{ cursor: 'pointer' }} onClick={() => handleSort('location')}>Location {getSortIcon('location')}</TableCell>
                                        <TableCell sx={{ cursor: 'pointer' }} onClick={() => handleSort('date')}>Event Date {getSortIcon('date')}</TableCell>
                                        <TableCell sx={{ cursor: 'pointer' }} onClick={() => handleSort('startDate')}>Start {getSortIcon('startDate')}</TableCell>
                                        <TableCell sx={{ cursor: 'pointer' }} onClick={() => handleSort('endDate')}>End {getSortIcon('endDate')}</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell sx={{ cursor: 'pointer' }} onClick={() => handleSort('createdAt')}>Created {getSortIcon('createdAt')}</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {events?.length ? events.map(ev => (
                                        <TableRow key={ev.id} hover>
                                             <TableCell><Typography variant="body2">{ev.id || '—'}</Typography></TableCell>
                                            <TableCell><Typography variant="body2">{ev.organizerName || '—'}</Typography></TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 240 }}>{ev.title || '—'}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 140 }}>{ev.category || '—'}</Typography>
                                            </TableCell>
                                            <TableCell><Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>{ev.locationType || '—'}</Typography></TableCell>
                                            <TableCell><Typography variant="caption">{ev.date ? new Date(ev.date).toLocaleDateString() : '—'}</Typography></TableCell>
                                            <TableCell><Typography variant="caption">{ev.startTime || '—'}</Typography></TableCell>
                                            <TableCell><Typography variant="caption">{ev.endTime || '—'}</Typography></TableCell>
                                            <TableCell>
                                                <Chip size="small" label={ev.status || 'UNKNOWN'} color={getStatusColor(ev.status)} sx={{ height: 22, fontSize: '0.7rem' }} />
                                            </TableCell>
                                            <TableCell><Typography variant="caption">{ev.createdAt ? new Date(ev.createdAt).toLocaleDateString() : '—'}</Typography></TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="View Details"><IconButton size="small" sx={{ p: 0.5 }} onClick={() => { setSelectedEvent(ev); setDetailsOpen(true); }}><VisibilityIcon fontSize="small" color="info" /></IconButton></Tooltip>
                                                <Tooltip title="Edit">
                                                    <IconButton
                                                        size="small"
                                                        sx={{ p: 0.5 }}
                                                        onClick={() => { setSelectedEvent(ev); setEditOpen(true); }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                {ev.status === 'CANCELLED' || ev.status === 'COMPLETED' ? null : (
                                                    ev.status === 'SUSPENDED' ? (
                                                        <Tooltip title="Activate">
                                                            <IconButton
                                                                size="small"
                                                                sx={{ p: 0.5 }}
                                                                onClick={() => { setSelectedEvent(ev); requestAction('activate'); }}
                                                            >
                                                                <CheckCircleIcon fontSize="small" color="success" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip title="Suspend">
                                                            <IconButton
                                                                size="small"
                                                                sx={{ p: 0.5 }}
                                                                onClick={() => { setSelectedEvent(ev); requestAction('suspend'); }}
                                                            >
                                                                <BlockIcon fontSize="small" color="warning" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )
                                                )}
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        sx={{ p: 0.5 }}
                                                        onClick={() => { setSelectedEvent(ev); requestAction('delete'); }}
                                                    >
                                                        <DeleteIcon fontSize="small" color="error" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    )) : (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                                <Typography variant="body1" color="text.secondary">No events found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            component="div"
                            count={pagination.totalElements}
                            rowsPerPage={pagination.size}
                            page={pagination.page}
                            onPageChange={handlePageChange}
                            onRowsPerPageChange={handleRowsChange}
                            sx={{ borderTop: '1px solid rgba(224,224,224,1)' }}
                        />
                    </>
                )}
            </Paper>

            {/* Menu */}
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={openDetails}><VisibilityIcon fontSize="small" sx={{ mr: 1 }} /> View Details</MenuItem>
                <MenuItem onClick={openEdit}><EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit Event</MenuItem>
                {selectedEvent?.status === 'SUSPENDED' ? (
                    <MenuItem onClick={() => requestAction('activate')}><CheckCircleIcon fontSize="small" sx={{ mr: 1 }} /> Activate</MenuItem>
                ) : (
                    <MenuItem onClick={() => requestAction('suspend')}><BlockIcon fontSize="small" sx={{ mr: 1 }} /> Suspend</MenuItem>
                )}
                <MenuItem onClick={() => requestAction('delete')}><DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete</MenuItem>
            </Menu>

            {/* Details Modal */}
            <EventDetailsModal
                open={detailsOpen}
                onClose={() => setDetailsOpen(false)}
                eventId={selectedEvent?.id}
            />

            {/* Edit Modal */}
            <EventEditModal
                open={editOpen}
                onClose={() => setEditOpen(false)}
                event={selectedEvent}
                onSaved={() => { showSnackbar('Event updated', 'success'); fetchEvents(); }}
            />

            {/* Confirmation Dialog */}
            <Dialog open={confirmOpen} onClose={closeConfirm}>
                <DialogTitle>{confirmAction ? `${confirmAction.charAt(0).toUpperCase() + confirmAction.slice(1)} Event` : 'Confirm'}</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to {confirmAction} this event?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeConfirm}>Cancel</Button>
                    <Button onClick={performAction} color={confirmAction === 'delete' ? 'error' : 'primary'} variant="contained">
                        {confirmAction && confirmAction.charAt(0).toUpperCase() + confirmAction.slice(1)}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AdminEventManagement;
